import React from 'react';
import * as Routes from '@/modules/routes';
import {
  RailPage, isCurrentPage, lessonPages, manageUnitsPage, programPages, sectionPages, unitPages,
} from '../SysadminSPA/shared/ProgramTree/contentPages';
import NodeTabs from '../SysadminSPA/shared/ProgramTree/NodeTabs';

// AIDEV-NOTE: The band above a sysadmin content page when the content rail is on: crumbs for the
// path you're in, the current node's title, and that node's pages as tabs. Replaces the fixed
// lesson tab strip and the Lessons & Units dropdown. Page lists come from contentPages.ts, the
// same source the rail reads, so the two can't disagree.

interface NodeRef {
  id: number;
  number?: string | null;
  title: string;
}

interface SectionRef extends NodeRef {
  notebooksCount: number;
  readingCheckQuestionsCount: number | null;
  readingGameId: number | null;
  tutorialId: number | null;
}

export interface SysadminContextBarProps {
  lesson?: NodeRef;
  program: NodeRef & { isScience: boolean };
  section?: SectionRef;
  unit?: NodeRef;
}

interface Crumb {
  href: string;
  label: string;
}

const numbered = (prefix: string, node: NodeRef) => (
  node.number ? `${prefix} ${node.number}` : node.title
);

const SysadminContextBar = ({ lesson, program, section, unit }: SysadminContextBarProps) => {
  const { id: programId } = program;
  // Crumbs are the ancestors of where you are, never the current node: the title names that.
  const crumbs: Crumb[] = [{ href: Routes.admin_programs_path(), label: 'Programs' }];
  let { title } = program;
  let pages: RailPage[] = [...programPages(programId), { ...manageUnitsPage(programId), label: 'Units' }];

  if (unit) {
    crumbs.push({ href: Routes.admin_program_lessons_path(programId), label: program.title });
    title = unit.number ? `Unit ${unit.number} · ${unit.title}` : unit.title;
    pages = unitPages(unit.id, programId);
  }
  if (lesson) {
    if (unit) {
      crumbs.push({
        href: Routes.edit_admin_unit_path(unit.id, { program_id: programId }),
        label: numbered('Unit', unit),
      });
    } else {
      crumbs.push({ href: Routes.admin_program_lessons_path(programId), label: program.title });
    }
    title = lesson.number ? `Lesson ${lesson.number} · ${lesson.title}` : lesson.title;
    pages = lessonPages(lesson.id, programId, program.isScience);
  }
  if (section && lesson) {
    crumbs.push({
      href: Routes.admin_lesson_sections_path(lesson.id, { program_id: programId }),
      label: numbered('Lesson', lesson),
    });
    ({ title } = section);
    pages = sectionPages({
      id: section.id,
      notebooks_count: section.notebooksCount,
      reading_check_questions_count: section.readingCheckQuestionsCount,
      reading_game_id: section.readingGameId,
      tutorial_id: section.tutorialId,
    }, lesson.id, programId);
  }

  const currentPage = pages.find(isCurrentPage);

  return (
    <div className="tw-mb-4 tw-border-0 tw-border-b tw-border-solid tw-border-spa-border tw-font-tci">
      <nav
        aria-label="Breadcrumb"
        className="tw-flex tw-flex-wrap tw-items-center tw-gap-1 tw-text-xs tw-text-spa-secondary"
      >
        {crumbs.map((crumb, index) => (
          <React.Fragment key={crumb.href}>
            {index > 0 && <span aria-hidden="true" className="tw-text-spa-tertiary">/</span>}
            <a className="tw-text-spa-secondary hover:tw-text-spa-on-bg" href={crumb.href}>{crumb.label}</a>
          </React.Fragment>
        ))}
      </nav>
      <h1 className="tw-my-2 tw-text-xl tw-font-bold tw-leading-snug tw-text-spa-heading">
        {title}
        {currentPage && (
          <span className="tw-font-normal tw-text-spa-secondary">{` · ${currentPage.label}`}</span>
        )}
      </h1>
      <NodeTabs pages={pages} />
    </div>
  );
};

export default SysadminContextBar;
